// Administración · {Tiendas y zonas} (plan 2026-10-30 §2.4, T-1iyh;
// handoff README §4, prototipo/sec-base.jsx SecActivos).
//
// Dos tabs sobre un panel: la tabla de {tiendas} (nombre, zona, marca,
// jefe de tienda, «Editar datos») y la de {zonas} (nombre, responsable,
// nº de tiendas, «Cambiar responsable», «Renombrar»). Un solo botón
// primario en la cabecera, que cambia con la tab («Nueva tienda» /
// «Nueva zona»). Toda palabra de vertical sale de la receta
// (features/administracion/activos/words.ts); nada por slug.
//
// El jefe de tienda se lee de donde lo lee el resto del producto:
// `attributes[labels.assetInfoFieldKey]` (lo escribe la importación de
// planilla y lo muestra /cartera), y si está vacío, el nombre de la cuenta
// vinculada (`storeManagerUserId`) desde el roster. «Sin asignar» sólo
// cuando no hay ninguno de los dos. El responsable de una zona viene en
// `subline` y se cruza con el roster; si el servidor mandó su comodín
// («Supervisor: Supervisor», resolver sin lookup) no se pinta como nombre.
//
// Lo que el handoff dibuja y el backend no tiene todavía no se muestra:
// «Importar planilla» (T-y2z3), la columna Código (`assets.code`), la
// columna Checklists (sin conteo por tienda en ningún endpoint barato) y
// «Archivar». La bajada de la cabecera es la del prototipo sin la mitad
// de la planilla. Ver los drawers para los campos que corren la misma
// suerte.
//
// La guarda de acceso (canAdministerOrg) la pone la ruta en App.tsx; esta
// pantalla asume que quien la ve puede administrar.

import { useMemo, useState } from "react";
import type { OrgMember } from "@bitacorapro/api-types";
import { EmptyState } from "../../components/EmptyState";
import { Icon } from "../../components/icons";
import { PageError } from "../../components/PageError";
import { PageHeader } from "../../components/PageHeader";
import { PageSkeleton } from "../../components/PageSkeleton";
import { Panel } from "../../components/Panel";
import { Skel } from "../../components/Skel";
import { SkelTable } from "../../components/skeleton/SkelTable";
import { Tabs, tabPanelProps } from "../../components/Tabs";
import {
  EditAssetDrawer,
  NewAssetDrawer,
} from "../../features/administracion/activos/AssetDrawers";
import {
  GroupLeadDrawer,
  GroupRenameDrawer,
  NewGroupDrawer,
} from "../../features/administracion/activos/GroupDrawers";
import type { AdminAssetGroupRow, AdminAssetRow } from "../../features/administracion/activos/api";
import { useAdminAssetGroups, useAdminAssets } from "../../features/administracion/activos/queries";
import {
  activosWords,
  assetLeadFromAttributes,
  groupLeadNameFromSubline,
} from "../../features/administracion/activos/words";
import { adminWords } from "../../lib/admin/words";
import { useRecipe, useRoster } from "../../lib/api/queries";
import { useQueryView } from "../../lib/loading/useQueryView";
import { ADMIN_TOAST_OPTS, useToast } from "../../lib/useToast";
import { normalizeSearch, storeTitle } from "../../lib/util";
import { gateOf, RefreshNotices, SlowNotice } from "../queryViewKit";

type Tab = "activos" | "grupos";
type GroupDrawer = { group: AdminAssetGroupRow; mode: "lead" | "name" };

const TABS_ID = "activos";
const NO_ASSETS: AdminAssetRow[] = [];
const NO_GROUPS: AdminAssetGroupRow[] = [];
const NO_MEMBERS: OrgMember[] = [];

function SinAsignar() {
  return <span className="st-pill st-pill--pending-evidence">Sin asignar</span>;
}

/**
 * El responsable de una zona según `subline` cruzado con el roster:
 * `none` sin subline; `resolved` cuando el nombre es el displayName de
 * alguien con rol de zona (y entonces hay id para preseleccionar);
 * `unresolved` cuando hay responsable pero el nombre no coincide con nadie
 * (el comodín «Supervisor» del servidor sin lookup, o alguien que ya no
 * está en el roster). Nunca se pinta un nombre que no sea de una persona.
 */
type GroupLead =
  | { kind: "none" }
  | { kind: "resolved"; name: string; userId: string }
  | { kind: "unresolved" };

export function Activos() {
  const recipeQuery = useRecipe();
  const assetsQuery = useAdminAssets();
  const groupsQuery = useAdminAssetGroups();
  const rosterQuery = useRoster();
  const toast = useToast();

  const recipe = recipeQuery.data;
  const w = useMemo(() => activosWords(recipe), [recipe]);
  const title = adminWords(recipe).sections.activos;

  const [tab, setTab] = useState<Tab>("activos");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState<AdminAssetRow | null>(null);
  const [nuevo, setNuevo] = useState(false);
  const [grp, setGrp] = useState<GroupDrawer | null>(null);

  // Las cuatro consultas son independientes y la pantalla necesita las
  // cuatro (las palabras, las dos tablas y los nombres del roster), así
  // que se pliegan en un solo gate: el primer error gana, luego el
  // skeleton, y la cabecera pinta desde el primer render (ADR-0166).
  const gate = gateOf([
    useQueryView(recipeQuery),
    useQueryView(assetsQuery),
    useQueryView(groupsQuery),
    useQueryView(rosterQuery),
  ]);

  function retryAll() {
    for (const query of [recipeQuery, assetsQuery, groupsQuery, rosterQuery]) {
      if (query.isError || query.isPending) void query.refetch();
    }
  }

  function saved(message: string) {
    setEdit(null);
    setNuevo(false);
    setGrp(null);
    toast.show(message, "ok", ADMIN_TOAST_OPTS);
  }

  // Constantes vacías con identidad estable mientras el gate no está
  // listo, para que los useMemo de abajo no recalculen en cada render.
  const ready = gate.kind === "ready";
  const assets = ready ? gate.data[1] : NO_ASSETS;
  const groups = ready ? gate.data[2] : NO_GROUPS;
  const members = ready ? gate.data[3].members : NO_MEMBERS;

  const groupNameById = useMemo(
    () => new Map<string, string>(groups.map((g) => [g.id, g.name])),
    [groups]
  );
  const memberNameById = useMemo(
    () => new Map(members.map((m: OrgMember) => [m.userId, m.displayName])),
    [members]
  );
  // displayName → userId, sólo entre quienes tienen rol de zona: es el
  // único cruce posible mientras GET /asset-groups no traiga el id.
  const groupLeadIdByName = useMemo(
    () =>
      new Map(
        members
          .filter((m: OrgMember) => w.groupLeadRoleKeys.includes(m.role))
          .map((m: OrgMember) => [m.displayName, m.userId])
      ),
    [members, w.groupLeadRoleKeys]
  );

  function assetLeadOf(a: AdminAssetRow): string | null {
    return (
      assetLeadFromAttributes(a.attributes, w.assetLeadKey) ??
      (a.storeManagerUserId ? (memberNameById.get(a.storeManagerUserId) ?? null) : null)
    );
  }

  function groupLeadOf(g: AdminAssetGroupRow): GroupLead {
    if (!w.groupLeadFromSubline) return { kind: "none" };
    // Con el id en el wire el cruce con el roster es directo; «sin
    // resolver» sólo si la cuenta ya no está en el roster. El `subline`
    // queda como respaldo para un servidor anterior a `supervisorUserId`.
    if (g.supervisorUserId !== undefined) {
      if (g.supervisorUserId === null) return { kind: "none" };
      const name = memberNameById.get(g.supervisorUserId);
      return name ? { kind: "resolved", name, userId: g.supervisorUserId } : { kind: "unresolved" };
    }
    const name = groupLeadNameFromSubline(g.subline);
    if (name === null) return { kind: "none" };
    const userId = groupLeadIdByName.get(name);
    return userId ? { kind: "resolved", name, userId } : { kind: "unresolved" };
  }

  const sortedAssets = useMemo(
    () =>
      [...assets].sort((a, b) =>
        storeTitle(a, "").localeCompare(storeTitle(b, ""), "es", { sensitivity: "base" })
      ),
    [assets]
  );
  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" })),
    [groups]
  );

  // Búsqueda local, sin tildes ni mayúsculas (normalizeSearch): la tabla
  // ya tiene todas las filas, no hay nada que pedir al servidor.
  const needle = normalizeSearch(q.trim());
  const visibleAssets = needle
    ? sortedAssets.filter((a) =>
        normalizeSearch(
          [
            storeTitle(a, ""),
            a.assetGroupId ? (groupNameById.get(a.assetGroupId) ?? "") : "",
            a.brand?.name ?? "",
            assetLeadOf(a) ?? "",
          ].join(" ")
        ).includes(needle)
      )
    : sortedAssets;
  const visibleGroups = needle
    ? sortedGroups.filter((g) => {
        const lead = groupLeadOf(g);
        return normalizeSearch(
          [g.name, lead.kind === "resolved" ? lead.name : ""].join(" ")
        ).includes(needle);
      })
    : sortedGroups;

  const primary =
    tab === "activos" ? (
      <button
        type="button"
        className="btn btn--primary"
        title={`Crear una ${w.assetOneLower} nueva, una a una`}
        onClick={() => setNuevo(true)}
      >
        <Icon.Plus />
        {w.assetNew}
      </button>
    ) : (
      <button
        type="button"
        className="btn btn--primary"
        title={`Crear una ${w.groupOneLower} nueva y elegir su responsable`}
        onClick={() => setNuevo(true)}
      >
        <Icon.Plus />
        {w.groupNew}
      </button>
    );

  function renderBody() {
    if (gate.kind === "error") {
      return gate.offline ? (
        <PageError
          title="Sin conexión"
          description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
          onRetry={gate.retry}
        />
      ) : (
        <PageError
          title={`No pudimos cargar tus ${w.assetManyLower}`}
          description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
          onRetry={gate.retry}
        />
      );
    }
    if (gate.kind !== "ready") {
      return (
        <>
          <PageSkeleton
            label={`Cargando tus ${w.assetManyLower}…`}
            visible={gate.kind === "loading" && gate.visible}
          >
            <ActivosSkeleton />
          </PageSkeleton>
          {gate.kind === "loading" && gate.slow && <SlowNotice onRetry={retryAll} />}
        </>
      );
    }
    return (
      <>
        <RefreshNotices
          refreshFailed={gate.refreshFailed}
          revalidatingHint={gate.revalidatingHint}
          onRetry={retryAll}
        />
        <Panel flush>
          <div className="filters">
            <Tabs<Tab>
              value={tab}
              onChange={(next) => {
                setTab(next);
                setQ("");
              }}
              idBase={TABS_ID}
              label={`Secciones de ${w.assetManyLower} y ${w.groupManyLower}`}
              tabs={[
                { value: "activos", label: w.assetMany, count: assets.length },
                { value: "grupos", label: w.groupMany, count: groups.length },
              ]}
            />
            <div className="filter-spacer" />
            <div className="filter-search">
              <Icon.Search s={14} />
              <input
                type="text"
                value={q}
                aria-label={`Buscar ${tab === "activos" ? w.assetManyLower : w.groupManyLower}`}
                placeholder={`Buscar ${tab === "activos" ? w.assetManyLower : w.groupManyLower}`}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
          {tab === "activos" ? (
            <div {...tabPanelProps(TABS_ID, "activos")}>
              {visibleAssets.length === 0 ? (
                needle ? (
                  <EmptyState
                    title={`Ninguna ${w.assetOneLower} coincide`}
                    description="Prueba con otro nombre."
                  />
                ) : (
                  <EmptyState
                    title={`Sin ${w.assetManyLower} todavía`}
                    description={`Crea la primera con «${w.assetNew}».`}
                  />
                )
              ) : (
                <table className="dtable">
                  <caption className="sr-only">Listado de {w.assetManyLower}</caption>
                  <thead>
                    <tr>
                      <th scope="col">{w.assetOne}</th>
                      <th scope="col">{w.groupOne}</th>
                      {w.brand && <th scope="col">{w.brand}</th>}
                      <th scope="col">{w.assetLead}</th>
                      <th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleAssets.map((a) => {
                      const name = storeTitle(a);
                      const lead = assetLeadOf(a);
                      return (
                        <tr key={a.id}>
                          <td className="cell-strong">{name}</td>
                          <td>
                            {a.assetGroupId ? (groupNameById.get(a.assetGroupId) ?? "—") : "—"}
                          </td>
                          {w.brand && <td>{a.brand?.name ?? "—"}</td>}
                          <td>{lead ?? <SinAsignar />}</td>
                          <td className="cell-actions">
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              title={`Cambiar nombre, ${w.groupOneLower} o dirección de ${name}`}
                              onClick={() => setEdit(a)}
                            >
                              Editar datos
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div {...tabPanelProps(TABS_ID, "grupos")}>
              {visibleGroups.length === 0 ? (
                needle ? (
                  <EmptyState
                    title={`Ninguna ${w.groupOneLower} coincide`}
                    description="Prueba con otro nombre."
                  />
                ) : (
                  <EmptyState
                    title={`Sin ${w.groupManyLower} todavía`}
                    description={`Crea la primera con «${w.groupNew}».`}
                  />
                )
              ) : (
                <table className="dtable">
                  <caption className="sr-only">Listado de {w.groupManyLower}</caption>
                  <thead>
                    <tr>
                      <th scope="col">{w.groupOne}</th>
                      <th scope="col">{w.groupLead}</th>
                      <th scope="col" className="col-num">
                        {w.assetMany}
                      </th>
                      <th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleGroups.map((g) => {
                      const lead = groupLeadOf(g);
                      return (
                        <tr key={g.id}>
                          <td className="cell-strong">{g.name}</td>
                          <td>
                            {lead.kind === "resolved" ? (
                              lead.name
                            ) : lead.kind === "unresolved" ? (
                              <span
                                className="ag-muted"
                                title={`Esta ${w.groupOneLower} tiene responsable, pero su nombre no está disponible en este listado.`}
                              >
                                Asignado · nombre no disponible
                              </span>
                            ) : (
                              <SinAsignar />
                            )}
                          </td>
                          <td className="col-num mono">{g.stats[0]?.n ?? 0}</td>
                          <td className="cell-actions">
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              title={`Quién supervisa ${g.name}`}
                              onClick={() => setGrp({ group: g, mode: "lead" })}
                            >
                              Cambiar responsable
                            </button>
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              title={`Cambiar el nombre; las ${w.assetManyLower} no se mueven`}
                              onClick={() => setGrp({ group: g, mode: "name" })}
                            >
                              Renombrar
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </Panel>
      </>
    );
  }

  const grpLead = grp ? groupLeadOf(grp.group) : null;

  return (
    <div className="page">
      <PageHeader
        eyebrow={adminWords(recipe).module}
        title={title}
        sub={
          recipeQuery.isPending ? (
            <Skel variant="text" width="55%" />
          ) : recipeQuery.isError ? undefined : (
            `Crea ${w.assetManyLower} una a una.`
          )
        }
        actions={ready ? primary : undefined}
      />
      {renderBody()}

      {edit && (
        <EditAssetDrawer
          asset={edit}
          groups={groups}
          leadName={assetLeadOf(edit)}
          words={w}
          onClose={() => setEdit(null)}
          onSaved={saved}
        />
      )}
      {nuevo && tab === "activos" && (
        <NewAssetDrawer groups={groups} words={w} onClose={() => setNuevo(false)} onSaved={saved} />
      )}
      {nuevo && tab === "grupos" && (
        <NewGroupDrawer
          members={members}
          words={w}
          onClose={() => setNuevo(false)}
          onSaved={saved}
        />
      )}
      {grp && grp.mode === "lead" && (
        <GroupLeadDrawer
          group={grp.group}
          currentLeadUserId={grpLead?.kind === "resolved" ? grpLead.userId : null}
          hasLead={grpLead !== null && grpLead.kind !== "none"}
          members={members}
          words={w}
          onClose={() => setGrp(null)}
          onSaved={saved}
        />
      )}
      {grp && grp.mode === "name" && (
        <GroupRenameDrawer
          group={grp.group}
          words={w}
          onClose={() => setGrp(null)}
          onSaved={saved}
        />
      )}
    </div>
  );
}

// El panel real con la barra de tabs y la tabla de {tiendas}: las
// cabeceras son barras porque dependen de la receta, que puede estar
// cargando a la vez.
function ActivosSkeleton() {
  return (
    <div className="panel">
      <div className="filters" aria-hidden="true">
        <Skel width={110} height={30} />
        <Skel width={90} height={30} />
        <div className="filter-spacer" />
        <Skel width={220} height={30} />
      </div>
      <SkelTable rows={8} cols={5} />
    </div>
  );
}

export default Activos;
